import { Injectable } from '@angular/core';
import { ProductionTranslationService } from '../../../core/services/production-translation.service';
import { getDisplayPrice } from '../../../core/utils/display-price';
import { UnifiedShipment, UnifiedPhoneInfo } from '../../../core/models/unified-shipment.models';
import { StatusMappingService } from '../../../core/services/status-mapping.service';

/**
 * 🎨 SHIPMENT FORMATTER SERVICE
 * ===============================
 *
 * Service centralisé pour tout le formatage et l'affichage des données shipment
 * - Formatage de téléphones
 * - Formatage de dates
 * - Formatage de prix
 * - Traduction de statuts
 * - Extraction de données complexes
 *
 * Élimine la logique de formatage des composants (Single Responsibility)
 *
 * @version 1.0.0
 * @author BeFret Development Team
 */
@Injectable({
  providedIn: 'root'
})
export class ShipmentFormatterService {

  constructor(
    private translate: ProductionTranslationService,
    private statusMapper: StatusMappingService
  ) {}

  // ===== PHONE FORMATTING =====

  /**
   * Formater un numéro de téléphone
   * Gère les anciens formats (string) et nouveaux (objet)
   */
  formatPhone(phone: UnifiedPhoneInfo | string | any): string {
    if (!phone) {
      return 'N/A';
    }

    // Si c'est déjà une string, la retourner
    if (typeof phone === 'string') {
      return phone;
    }

    // Structure UnifiedPhoneInfo: { prefix, number, whatsapp }
    if (phone.prefix && phone.number) {
      return `${phone.prefix} ${phone.number}`;
    }

    // Format legacy ou invalide
    return phone.formatted || phone.number || 'N/A';
  }

  /**
   * Vérifier si un téléphone est WhatsApp
   */
  isWhatsAppEnabled(phone: UnifiedPhoneInfo | any): boolean {
    if (!phone || typeof phone === 'string') {
      return false;
    }
    return phone.whatsapp === true;
  }

  // ===== DATE FORMATTING =====

  /**
   * Formater une date ISO en format lisible
   */
  formatDate(dateString: string | undefined, locale = 'fr-FR'): string {
    if (!dateString) {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);

      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }

      return date.toLocaleString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date invalide';
    }
  }

  /**
   * Formater une date en format court (sans heure)
   */
  formatDateShort(dateString: string | undefined, locale = 'fr-FR'): string {
    if (!dateString) {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }

      return date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date invalide';
    }
  }

  /**
   * Formater un timestamp relatif (il y a X jours/heures)
   */
  formatRelativeTime(dateString: string | undefined): string {
    if (!dateString) {
      return 'N/A';
    }

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) {
        return 'À l\'instant';
      } else if (diffMins < 60) {
        return `Il y a ${diffMins} min`;
      } else if (diffHours < 24) {
        return `Il y a ${diffHours}h`;
      } else if (diffDays < 7) {
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
      } else {
        return this.formatDateShort(dateString);
      }
    } catch (error) {
      return 'N/A';
    }
  }

  // ===== PRICE FORMATTING =====

  /**
   * Formater un prix avec devise
   */
  formatPrice(amount: number | undefined, currency = 'EUR'): string {
    if (amount === undefined || amount === null) {
      return 'N/A';
    }

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Extraire le prix total d'un shipment
   */
  getTotalPrice(shipment: UnifiedShipment): number {
    // #374 — prix payé, remise incluse (garde paymentStatus='paid').
    return getDisplayPrice(shipment as any);
  }

  /**
   * Extraire la devise d'un shipment
   */
  getCurrency(shipment: UnifiedShipment): string {
    return (shipment as any)?.standardData?.pricing?.currency || 'EUR';
  }

  // ===== STATUS FORMATTING =====

  /**
   * ✅ MIGRÉ vers StatusMappingService
   * Traduire un statut en label lisible
   */
  getStatusLabel(status: string, category?: string, webhookStatus?: any): string {
    // Mapper vers le statut unifié
    const unifiedStatus = this.statusMapper.mapFirebaseStatusToUnified(status, category, webhookStatus);

    // Obtenir le label traduit directement depuis StatusMappingService
    return this.statusMapper.getStatusLabel(unifiedStatus);
  }

  /**
   * ✅ NOUVEAU: Obtenir le timeline step simplifié pour le client
   * Convertit les 99 statuts détaillés en 6 étapes client
   */
  getTimelineStep(status: string, category?: string, webhookStatus?: any): string {
    // Mapper vers le statut unifié
    const unifiedStatus = this.statusMapper.mapFirebaseStatusToUnified(status, category, webhookStatus);

    // Mapper vers timeline step (6 étapes simplifiées)
    return this.statusMapper.mapUnifiedToTimelineStep(unifiedStatus);
  }

  /**
   * ✅ MIGRÉ vers StatusMappingService
   * Obtenir la classe CSS pour un statut
   */
  getStatusClass(status: string, category?: string, webhookStatus?: any): string {
    // Mapper vers le statut unifié
    const unifiedStatus = this.statusMapper.mapFirebaseStatusToUnified(status, category, webhookStatus);

    // Obtenir la couleur du metadata
    const color = this.statusMapper.getStatusColor(unifiedStatus);

    // Mapper couleur → classe CSS Bootstrap
    const colorToClassMap: { [key: string]: string } = {
      'green': 'status-success',
      'blue': 'status-info',
      'orange': 'status-warning',
      'red': 'status-danger',
      'gray': 'status-default',
      'yellow': 'status-primary'
    };

    return colorToClassMap[color] || 'status-default';
  }

  // ===== ADDRESS FORMATTING =====

  /**
   * Formater une adresse complète
   */
  formatAddress(address: any): string {
    if (!address) {
      return 'N/A';
    }

    const parts = [
      address.street,
      address.zipCode,
      address.city,
      address.country
    ].filter(part => part && part.trim() !== '');

    return parts.join(', ') || 'N/A';
  }

  // ===== WEBHOOK STATUS HELPERS =====

  getWebhookOverallStatus(shipment: UnifiedShipment): string {
    return shipment.webhookStatus?.overall || 'N/A';
  }

  getWebhookPaymentStatus(shipment: UnifiedShipment): string {
    return shipment.webhookStatus?.stages?.payment?.status || 'N/A';
  }

  getWebhookDpdStatus(shipment: UnifiedShipment): string {
    return shipment.webhookStatus?.stages?.dpd?.status || 'N/A';
  }

  getWebhookCollectionStatus(shipment: UnifiedShipment): string {
    return shipment.webhookStatus?.stages?.collection?.status || 'N/A';
  }

  getWebhookLabelStatus(shipment: UnifiedShipment): string {
    return shipment.webhookStatus?.stages?.label?.status || 'N/A';
  }

  getWebhookNotificationsEmail(shipment: UnifiedShipment): string {
    const sent = shipment.webhookStatus?.stages?.notifications?.email;
    return sent ? 'sent' : 'pending';
  }

  getWebhookNotificationsSms(shipment: UnifiedShipment): string {
    const sent = shipment.webhookStatus?.stages?.notifications?.sms;
    return sent ? 'sent' : 'pending';
  }

  getWebhookActionRequired(shipment: UnifiedShipment): boolean {
    return shipment.webhookStatus?.actionRequired || false;
  }

  getWebhookContactSupport(shipment: UnifiedShipment): boolean {
    return shipment.webhookStatus?.contactSupport || false;
  }

  // ===== METADATA HELPERS =====

  getMetadataVersion(shipment: UnifiedShipment): string {
    return shipment.metadata?.version || 'N/A';
  }

  getMetadataSource(shipment: UnifiedShipment): string {
    return shipment.metadata?.source || 'N/A';
  }

  getMetadataEnvironment(shipment: UnifiedShipment): string {
    return shipment.metadata?.environment || 'N/A';
  }

  getMetadataFlags(shipment: UnifiedShipment): any {
    return shipment.metadata?.flags || {};
  }

  getMetadataTags(shipment: UnifiedShipment): string[] {
    return shipment.metadata?.tags || [];
  }

  // ===== TIMELINE & HISTORY HELPERS =====

  getStatusHistory(shipment: UnifiedShipment): any[] {
    return shipment.statusHistory || [];
  }

  getPhaseTimeline(shipment: UnifiedShipment): any[] {
    return shipment.phase?.timeline || [];
  }

  getPhaseProgress(shipment: UnifiedShipment): number {
    return shipment.phase?.progress || 0;
  }

  // ===== DPD INTEGRATION HELPERS =====

  getDpdPickupPoint(shipment: UnifiedShipment): any {
    return (shipment as any)?.standardData?.dpdIntegration?.pickupPoint ||
           (shipment as any)?.standardData?.pickupPoint ||
           null;
  }

  getDpdIntegration(shipment: UnifiedShipment): any {
    return (shipment as any)?.standardData?.dpdIntegration || null;
  }

  // ===== PAYMENT FLOW HELPERS =====

  getPaymentFlow(shipment: UnifiedShipment): any {
    return shipment.paymentFlow || null;
  }

  // ===== PARCEL OPTIONS HELPERS =====

  getParcelOptions(shipment: UnifiedShipment): any {
    return shipment.parcelInfo?.options || {};
  }

  // ===== EXISTENCE CHECKS =====

  hasWebhookStatus(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus;
  }

  hasMetadata(shipment: UnifiedShipment): boolean {
    return !!shipment.metadata;
  }

  hasStatusHistory(shipment: UnifiedShipment): boolean {
    return !!(shipment.statusHistory && shipment.statusHistory.length > 0);
  }

  hasPhaseTimeline(shipment: UnifiedShipment): boolean {
    return !!(shipment.phase?.timeline && shipment.phase.timeline.length > 0);
  }

  hasDpdIntegration(shipment: UnifiedShipment): boolean {
    return !!((shipment as any)?.standardData?.dpdIntegration || (shipment as any)?.standardData?.dpdShipmentId);
  }

  hasPaymentFlow(shipment: UnifiedShipment): boolean {
    return !!shipment.paymentFlow;
  }

  // Stage existence checks
  hasWebhookPaymentStage(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus?.stages?.payment;
  }

  hasWebhookDpdStage(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus?.stages?.dpd;
  }

  hasWebhookCollectionStage(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus?.stages?.collection;
  }

  hasWebhookLabelStage(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus?.stages?.label;
  }

  hasWebhookNotificationsStage(shipment: UnifiedShipment): boolean {
    return !!shipment.webhookStatus?.stages?.notifications;
  }
}
